import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';

import { fetchQuery, graphql } from 'react-relay';

import type { ConvoAIResponseState, ConvoAIStreamError } from '@atlassian/ai-agent-streaming';
import { useConfluencePageAri } from '@atlassian/ai-summary';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useProductDependencies } from '../../../../controllers/use-product-dependencies';
import { AIDescriptionButton } from '../ai-description-button';

import type { aiDescriptionButtonWrapperQuery } from './__generated__/aiDescriptionButtonWrapperQuery.graphql';

// If linkResourceType is 'space', then it's a Space Overview Page, which is type "page".
// If linkResourceType is a 'blog', then we update to "blogpost" for pageAri
const getContentTypeForPageAri = (linkResourceType: string): string => {
	switch (linkResourceType) {
		case 'blog':
			return 'blogpost';
		case 'space':
			return 'page';
		default:
			return linkResourceType;
	}
};

type AIDescriptionButtonWrapperProps = {
	analyticsSource: string;
	confluencePageId: string;
	isAILoading?: boolean;
	linkResourceType: string;
	setAIDescriptionContent: Dispatch<SetStateAction<string | undefined | null>>;
	setAIError: Dispatch<SetStateAction<ConvoAIStreamError | undefined>>;
	setAIResponseState: Dispatch<SetStateAction<ConvoAIResponseState | undefined>>;
	setIsAILoading: Dispatch<SetStateAction<boolean | undefined>>;
};

export const AIDescriptionButtonWrapper = ({
	confluencePageId,
	linkResourceType,
	isAILoading,
	setIsAILoading,
	setAIError,
	setAIDescriptionContent,
	setAIResponseState,
	analyticsSource,
}: AIDescriptionButtonWrapperProps): false | "" | React.JSX.Element | null | undefined => {
	const [loading, setLoading] = useState(true);
	const [queriedHomepageId, setQueriedHomepageId] = useState<string>('');
	const [errorComponent, setErrorComponent] = useState<JSX.Element | null>(null);

	const { cloudId, isAdminHubAIEnabled, errorDisplayComponent } = useProductDependencies();
	const environment = useRelayEnvironmentProvider().getEnvironment({});

	const querySpaceOverviewHomepageId = useCallback(async () => {
		// If it's not a space overview page, we don't need to fetch the homepageId
		if (linkResourceType !== 'space') {
			setLoading(false);
			return;
		}

		setLoading(true);
		try {
			const spaceOverviewData = await fetchQuery<aiDescriptionButtonWrapperQuery>(
				environment,
				graphql`
					query aiDescriptionButtonWrapperQuery($spaceId: ID!) {
						space(id: $spaceId) {
							homepageId
						}
					}
				`,
				{ spaceId: confluencePageId },
			).toPromise();

			setQueriedHomepageId(spaceOverviewData?.space?.homepageId ?? '');
		} catch (error) {
			if (errorDisplayComponent) {
				const ErrorComponent = errorDisplayComponent;
				setErrorComponent(<ErrorComponent error={error as Error} />);
			}
		} finally {
			setLoading(false);
		}
	}, [linkResourceType, confluencePageId, environment, errorDisplayComponent]);

	useEffect(() => {
		void querySpaceOverviewHomepageId();
	}, [querySpaceOverviewHomepageId]);

	const contentType = getContentTypeForPageAri(linkResourceType);
	const isPageOrBlog = contentType === 'page' || contentType === 'blogpost';

	const pageId = linkResourceType === 'space' ? queriedHomepageId : confluencePageId;

	const { pageAri } = useConfluencePageAri(cloudId, pageId, contentType);

	if (errorComponent) {
		return errorComponent;
	}

	const shouldRenderAIButton = isAdminHubAIEnabled && !loading && isPageOrBlog;

	return (
		shouldRenderAIButton &&
		pageAri && (
			<AIDescriptionButton
				pageAri={pageAri}
				confluencePageId={confluencePageId}
				isAILoading={isAILoading}
				setIsAILoading={setIsAILoading}
				setAIError={setAIError}
				setAIDescriptionContent={setAIDescriptionContent}
				setAIResponseState={setAIResponseState}
				analyticsSource={analyticsSource}
			/>
		)
	);
};
